import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Archive, ChevronRight, Home, ShieldCheck, XCircle } from 'lucide-react';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { fetchPanicHistory, type PanicLogSummary } from './api';

type HistoryBucket = 'resolved' | 'cancelled';

function formatStamp(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function HistoryRow({ log }: { log: PanicLogSummary }): JSX.Element {
  const closed = log.status === 'cancelled';

  return (
    <Link
      to={`/history/incidents/${log.panic_log_id}`}
      className="group flex items-start justify-between gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3.5 transition-colors hover:border-neutral-300 hover:bg-neutral-50"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {closed ? (
            <XCircle className="h-4 w-4 shrink-0 text-neutral-400" strokeWidth={2.5} />
          ) : (
            <ShieldCheck className="h-4 w-4 shrink-0 text-green-600" strokeWidth={2.5} />
          )}
          <p className="truncate text-base font-bold text-ink">{log.emergency_type_name}</p>
        </div>
        <p className="mt-0.5 truncate text-sm font-medium text-neutral-600">
          {log.triggered_by_name?.trim() || 'Unknown member'}
        </p>
        {log.triggered_by_address?.trim() ? (
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-neutral-500">
            <Home className="h-3.5 w-3.5 shrink-0 text-neutral-400" strokeWidth={2} />
            {log.triggered_by_address}
          </p>
        ) : null}
        <p className="mt-1 text-[11px] font-medium tabular-nums text-neutral-400">
          Raised {formatStamp(log.created_at)}
          <span className="mx-1.5 text-neutral-300">·</span>
          {closed ? 'Cancelled' : 'Resolved'} {formatStamp(log.last_lifecycle_event_at)}
        </p>
      </div>
      <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-neutral-400 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

/** Closed incidents, split the way the tenant API buckets them. */
export function HistoryPage(): JSX.Element {
  const [bucket, setBucket] = useState<HistoryBucket>('resolved');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['panic', 'history', bucket],
    queryFn: () => fetchPanicHistory(bucket),
  });

  const logs = data ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <div className="mb-4 flex items-center gap-2">
        <Archive className="h-5 w-5 text-neutral-700" strokeWidth={2} />
        <h1 className="text-sm font-bold uppercase tracking-wider text-ink">History</h1>
      </div>

      <div className="mb-5 inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-1">
        {(['resolved', 'cancelled'] as HistoryBucket[]).map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => setBucket(b)}
            className={`rounded-md px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
              bucket === b ? 'bg-white text-ink shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {b}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {isError && (
        <ErrorState
          title="Could not load history"
          description={error instanceof Error ? error.message : 'Request failed'}
          onRetry={() => void refetch()}
        />
      )}

      {!isLoading && !isError && logs.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-neutral-200 px-6 py-12 text-center">
          <Archive className="mx-auto h-10 w-10 text-neutral-300" strokeWidth={1.5} />
          <p className="mt-3 text-sm font-semibold text-neutral-600">No {bucket} incidents yet.</p>
        </div>
      )}

      {logs.length > 0 && (
        <div className="space-y-3">
          {logs.map((log) => (
            <HistoryRow key={log.panic_log_id} log={log} />
          ))}
        </div>
      )}
    </div>
  );
}
